import { SetEveryPortService } from '../../../Checkout/store/everyPortServices/actions';
import { LoungeSelectedService } from './types';
import { CheckinPassengerFragment, NASProductCategory, NASProductFragment, PassengerFragment } from '@websky/graphql';
import { PassengerType } from '../../../enums';
import { CdnIconNames } from '@shared/ui';

/**
 * В качестве ключа используем сочетание product.id-fare.id
 * т.к. у разных сервисов с разным product.id может совпадать fare.id
 */
const getLoungeServiceKey = (service: LoungeSelectedService) => {
	return `${service.product.id}-${service.fare.id}`;
};

export const getEveryPortSelectedServices = (state: LoungeSelectedService[]): SetEveryPortService[] => {
	const travellerIdsByKey = new Map<string, Set<string>>();
	const servicesByKey = new Map<string, LoungeSelectedService>();

	state.forEach(item => {
		const key = getLoungeServiceKey(item);

		/** Для каждого ключа сохраняем id всех выбранных пассажиров */
		let travellerIds = travellerIdsByKey.get(key);

		if (!travellerIds) {
			travellerIds = new Set<string>();
			travellerIdsByKey.set(key, travellerIds);
		}

		travellerIds.add(item.travellerId);

		/** Для каждого ключа сохраняем данные сервиса */
		if (!servicesByKey.has(key)) {
			servicesByKey.set(key, item);
		}
	});

	/** Формируем массив сервисов с id выбранных пассажиров и данными о сервисе */
	return [...travellerIdsByKey.entries()].reduce<SetEveryPortService[]>((everyPortServices, [key, travellerIds]) => {
		const item = servicesByKey.get(key);

		if (item) {
			everyPortServices.push({
				product: item.product,
				fare: item.fare,
				travellerIds: [...travellerIds],
				additionalServices: [],
				orders: []
			});
		}

		return everyPortServices;
	}, []);
};

export const filterServicesForAdultTravellerExists = (
	state: LoungeSelectedService[],
	travellers: PassengerFragment[]
): LoungeSelectedService[] => {
	const travellersBySegment = new Map<string, LoungeSelectedService[]>();

	state.forEach(item => {
		travellersBySegment.set(item.product.segmentId.toString(), [
			...(travellersBySegment.get(item.product.segmentId.toString()) || []),
			item
		]);
	});

	const filteredServices: LoungeSelectedService[] = [];

	travellersBySegment.forEach((servicesOnSegment, segmentId) => {
		if (
			servicesOnSegment.some(
				service =>
					travellers.find(traveller => traveller.id === service.travellerId)?.type === PassengerType.Adult
			)
		) {
			filteredServices.push(...servicesOnSegment);
		}
	});

	return filteredServices;
};

export const findMinPrice = (
	product: NASProductFragment,
	travellers: (PassengerFragment | CheckinPassengerFragment)[]
) => {
	const firstPrice = product.fares?.[0]?.prices?.[0]?.price;

	const minPrice = {
		amount: firstPrice?.amount ?? 0,
		currency: firstPrice?.currency
	};

	product.fares.forEach(fare =>
		fare.prices
			.filter(
				price => travellers.find(traveller => traveller.id === price.traveller.id)?.type === PassengerType.Adult
			)
			.forEach(({ price }) => {
				if (price.amount > 0 && price.amount < minPrice.amount) {
					minPrice.amount = price.amount;
				}
			})
	);

	return minPrice;
};

const NasServicesIcons: Record<NASProductCategory, CdnIconNames[]> = {
	[NASProductCategory.fast_track]: ['Document/Passport', 'Info/Timer'],
	[NASProductCategory.vip]: ['Airport/Control', 'Amenity/WiFi', 'Services/Transfer', 'Services/Alcohol'],
	[NASProductCategory.lounge]: ['Amenity/WiFi', 'Services/Meal 2', 'Services/Alcohol'],
	[NASProductCategory.meet_and_greet]: ['Airport/Control', 'Services/Transfer', 'Document/Passport']
};

const NasServicesTitle = {
	[NASProductCategory.fast_track]: 'Fast track',
	[NASProductCategory.vip]: 'VIP',
	[NASProductCategory.lounge]: 'Business lounge',
	[NASProductCategory.meet_and_greet]: 'Meet and greet'
};

const NasServicesDescription = {
	[NASProductCategory.fast_track]: 'Pass pre-boarding security check faster',
	[NASProductCategory.vip]: 'Services of the highest comfort level',
	[NASProductCategory.lounge]: 'For your comfortable stay while waiting for a flight',
	[NASProductCategory.meet_and_greet]: 'For your comfortable stay while waiting for a flight'
};

export const getNasServiceCardParams = (category: NASProductCategory) => {
	return {
		icons: NasServicesIcons[category],
		title: NasServicesTitle[category],
		description: NasServicesDescription[category]
	};
};
